export interface GetAllExperimentsResponse {
  experiments: ExperimentDescription[];
}

export interface ExperimentDescription {
  name: string;
  enabled: boolean;
  description: string;
}

/**
 * TypeScript definitions for experiment analysis results
 * Generated from CSV data structure
 */

/**
 * Represents a single row of experiment analysis results
 */
export interface ExperimentResult {
  /** Segment of the analysis (e.g., Full) */
  segment: string;

  /** Subset of data used in the analysis (e.g., All, Fresh Orders) */
  dataSubset: string;

  /** Type of model used (e.g., TW & unit fe) */
  modelName: string;

  /** Type of covariance used in the statistical analysis (e.g., HC1) */
  covarianceType: string;

  /** Clustering method for standard errors, if applicable */
  seCluster?: string;

  /** Metric being measured (e.g., Mean_Delivery_Time) */
  metric: string;

  /** Baseline mean value for the metric */
  meanBaseline?: number;

  /** Percentage change from baseline */
  percChange: number;

  /** Effect size of the treatment */
  effectSizeTreatment: number;

  /** P-value for statistical significance */
  pVal: number;

  /** Lower bound of confidence interval */
  ciLower: number;

  /** Upper bound of confidence interval */
  ciUpper: number;
}

/**
 * Helper function to parse CSV string into ExperimentResult[]
 * @param csvContent The CSV content as a string
 * @returns Array of ExperimentResult objects
 */
export function parseExperimentResults(csvContent: string): ExperimentResult[] {
  const lines = csvContent.split('\n');
  const results: ExperimentResult[] = [];

  // Skip header line
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < lines.length; i++) {
    const line = (lines[i] ?? '').trim();
    if (!line) continue;

    const values = line.split(',');
    if (values.length < 12) continue;

    // Check if meanBaseline has a special message
    const meanBaselineValue =
      values[6] && values[6].startsWith('"')
        ? null
        : parseFloat(values[6] || '0');

    const metric = values[5] || '';

    const result: ExperimentResult = {
      segment: values[0] || '',
      dataSubset: values[1] || '',
      modelName: values[2] || '',
      covarianceType: values[3] || '',
      seCluster: values[4] || undefined,
      metric,
      meanBaseline: meanBaselineValue as number,
      percChange: values[7] ? parseFloat(values[7]) : 0,
      effectSizeTreatment: values[8] ? parseFloat(values[8]) : 0,
      pVal: values[9] ? parseFloat(values[9]) : 0,
      ciLower: values[10] ? parseFloat(values[10]) : 0,
      ciUpper: values[11] ? parseFloat(values[11]) : 0,
    };

    results.push(result);
  }

  return results;
}
